import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowRight } from "lucide-react";
import { BUSINESS, IMAGES } from "../lib/site";

const HERO_LINES = ["STUMPS", "DON'T STAND", "A CHANCE."];

const lineVariants = {
    hidden: { y: "115%", rotate: 2 },
    show: (i) => ({
        y: "0%",
        rotate: 0,
        transition: { duration: 0.9, delay: 0.25 + i * 0.13, ease: [0.16, 1, 0.3, 1] },
    }),
};

const HeroSection = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

    return (
        <section
            ref={ref}
            className="relative min-h-[100svh] flex flex-col justify-end overflow-hidden bg-brand-ink"
            data-testid="hero-section"
        >
            <motion.div className="absolute inset-0" style={{ y: bgY }}>
                <img
                    src={IMAGES.heroRings}
                    alt="Close-up of hardwood stump cross-section with growth rings"
                    className="w-full h-full object-cover scale-110"
                />
            </motion.div>
            <div className="absolute inset-0 bg-gradient-to-b from-brand-ink/80 via-brand-ink/55 to-brand-ink" />
            <div className="absolute inset-0 bg-gradient-to-r from-brand-ink/70 via-transparent to-transparent" />

            <motion.div
                style={{ opacity: fade }}
                className="relative z-10 max-w-7xl mx-auto w-full px-5 sm:px-8 pt-36 pb-16"
            >
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.1 }}
                    className="inline-flex items-center gap-2 border border-brand-line bg-brand-ink/70 backdrop-blur rounded-full px-4 py-2 mb-8"
                    data-testid="hero-badge"
                >
                    <span className="w-2 h-2 rounded-full bg-brand-amber animate-pulse" />
                    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-brand-cream">
                        Poplar Bluff, MO · 30-Mile Radius · Same-Week Guarantee
                    </span>
                </motion.div>

                <h1 className="font-display font-black uppercase text-brand-cream leading-[0.88] tracking-tight text-[clamp(3.8rem,12vw,10.5rem)]">
                    {HERO_LINES.map((line, i) => (
                        <span key={line} className="block overflow-hidden pb-1">
                            <motion.span
                                className="block origin-bottom-left"
                                custom={i}
                                variants={lineVariants}
                                initial="hidden"
                                animate="show"
                            >
                                {i === 2 ? (
                                    <>
                                        A{" "}
                                        <span className="text-brand-amber">CHANCE.</span>
                                    </>
                                ) : (
                                    line
                                )}
                            </motion.span>
                        </span>
                    ))}
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.8 }}
                    className="mt-6 max-w-xl text-brand-sage text-base sm:text-lg leading-relaxed"
                >
                    Professional stump grinding and full cleanup for Poplar Bluff
                    and a 30-mile radius. Scheduled the same week you call,
                    priced straight off the spec sheet.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.95 }}
                    className="mt-9 flex flex-col sm:flex-row gap-4"
                >
                    <a
                        href={BUSINESS.phoneTel}
                        data-testid="hero-call-cta"
                        className="group inline-flex items-center justify-center gap-3 bg-brand-amber text-brand-ink font-bold text-base sm:text-lg px-8 py-4 rounded-full hover:bg-brand-amberHover transition-all duration-300 hover:gap-4"
                    >
                        <Phone className="w-5 h-5" />
                        Call {BUSINESS.phoneDisplay}
                    </a>
                    <Link
                        to="/quote"
                        data-testid="hero-quote-cta"
                        className="group inline-flex items-center justify-center gap-3 border border-brand-cream/30 text-brand-cream font-semibold text-base sm:text-lg px-8 py-4 rounded-full hover:border-brand-amber hover:text-brand-amber transition-all duration-300"
                    >
                        Free Quote
                        <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                </motion.div>
            </motion.div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 1.2 }}
                className="relative z-10 border-t border-brand-cream/10"
            >
                <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex flex-wrap justify-between gap-2 font-mono text-[10px] sm:text-xs uppercase tracking-[0.2em] text-brand-moss">
                    <span>Butler County, Missouri</span>
                    <span>36.757° N / 90.394° W</span>
                    <span className="hidden sm:inline">Mon–Sat · 7AM–6PM</span>
                </div>
            </motion.div>
        </section>
    );
};

export default HeroSection;
